import { useState } from "react";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function WithholdDepositDialog({ booking, onClose, onSuccess }) {
  const [reason, setReason] = useState("");
  const [amount, setAmount] = useState(String(Number(booking?.deposit || 0).toFixed(2)));
  const [evidence, setEvidence] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const open = !!booking;
  const max = Number(booking?.deposit || 0);

  const close = () => {
    setReason(""); setAmount(""); setEvidence([]);
    onClose?.();
  };

  const uploadEvidence = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (evidence.length + files.length > 8) { toast.error("Up to 8 photos"); return; }
    setUploading(true);
    try {
      const paths = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        paths.push(data.path);
      }
      setEvidence((prev) => [...prev, ...paths]);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const removeEvidence = (idx) => setEvidence((prev) => prev.filter((_, i) => i !== idx));

  const submit = async () => {
    const trimmed = reason.trim();
    if (trimmed.length < 3) { toast.error("Please provide a reason (at least 3 characters)"); return; }
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0 || amt > max + 1e-6) {
      toast.error(`Amount must be between €0.01 and €${max.toFixed(2)}`);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/deposit/withhold`, {
        reason: trimmed,
        amount: Math.round(amt * 100) / 100,
        evidence_paths: evidence,
      });
      const accepted = Array.isArray(data?.evidence_paths) ? data.evidence_paths.length : evidence.length;
      if (evidence.length > 0 && accepted < evidence.length) {
        toast.warning(`${accepted} of ${evidence.length} evidence photos accepted (others were rejected)`);
      }
      toast.success(amt < max ? `€${amt.toFixed(2)} withheld, €${(max - amt).toFixed(2)} refunded` : "Deposit withheld");
      close();
      onSuccess?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const amt = Number(amount);
  const refundPreview = Number.isFinite(amt) && amt > 0 ? Math.max(0, max - amt) : null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Withhold deposit</DialogTitle>
          <DialogDescription>Choose an amount, give a reason, and optionally attach photos as evidence.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-neutral-600">
            Choose how much of the €{max.toFixed(2)} deposit you're withholding. The remainder is refunded to the customer.
          </p>

          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Amount to withhold (€)</Label>
            <Input
              type="number" min="0.01" step="0.01" max={max}
              className="mt-1 rounded-xl"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="withhold-amount-input"
            />
            {refundPreview !== null && (
              <div className="text-xs text-neutral-500 mt-1.5" data-testid="withhold-summary">
                Withhold <span className="font-semibold text-rose-700">€{Math.min(amt, max).toFixed(2)}</span>
                {refundPreview > 0 && (<> · refund <span className="font-semibold text-emerald-700">€{refundPreview.toFixed(2)}</span> to customer</>)}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Reason</Label>
            <Textarea
              rows={3}
              className="mt-1 rounded-xl"
              placeholder="e.g. Traffic fine ticket #12345 received after drop-off, or Scratch on rear bumper."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="withhold-reason-input"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Evidence photos (optional)</Label>
            <p className="text-xs text-neutral-500 mt-1 mb-2">Upload up to 8 photos — damage close-ups, traffic-fine document, fuel receipt, etc.</p>
            <div className="flex flex-wrap gap-2">
              {evidence.map((p, i) => (
                <div key={p} className="relative group">
                  <img src={p.startsWith("http") ? p : fileUrl(p)} alt="" className="h-16 w-16 rounded-xl object-cover border border-border" />
                  <button
                    type="button"
                    onClick={() => removeEvidence(i)}
                    className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-neutral-900 text-white flex items-center justify-center text-[10px]"
                    data-testid={`remove-evidence-${i}`}
                    aria-label="Remove evidence"
                  >
                    <Trash2 className="h-2.5 w-2.5" />
                  </button>
                </div>
              ))}
              {evidence.length < 8 && (
                <label className="h-16 w-16 rounded-xl border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:bg-neutral-50">
                  <input
                    type="file" accept="image/*" multiple
                    className="hidden"
                    onChange={uploadEvidence}
                    disabled={uploading}
                    data-testid="evidence-upload"
                  />
                  <Upload className="h-4 w-4 text-neutral-500" />
                </label>
              )}
            </div>
            {uploading && <div className="text-xs text-neutral-500 mt-1">Uploading…</div>}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={close} className="rounded-full">Cancel</Button>
            <Button
              onClick={submit}
              disabled={saving || uploading}
              className="rounded-full bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-withhold-btn"
            >
              {saving ? "Saving…" : "Withhold deposit"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
